import type { LiveState } from '@models/api';
import { getDetailsSpaceId, getErrorCode, getErrorDetails } from '@utils/errorDetection';

/**
 * The poll routes' error codes (polls-run CONTRACT § 4.0), read from the APIError envelope's
 * `detail.error_code`. One helper for every poll surface, so a code is named in one place; the
 * answer each surface gives a code lives with that surface (the vote's in `usePollVote`).
 */
export const POLL_ERROR_CODES = {
  closed: 'POLL_CLOSED',
  optionUnknown: 'POLL_OPTION_UNKNOWN',
  tooManyOptions: 'POLL_TOO_MANY_OPTIONS',
  notClosed: 'POLL_NOT_CLOSED',
  eventClosed: 'POLL_EVENT_CLOSED',
  endForbidden: 'POLL_END_FORBIDDEN',
  reopenForbidden: 'POLL_REOPEN_FORBIDDEN',
  votersForbidden: 'POLL_VOTERS_FORBIDDEN',
  locked: 'POLL_LOCKED',
  pinRequired: 'POLL_PIN_REQUIRED',
  cannotJoin: 'POLL_CANNOT_JOIN',
  joinPinInvalid: 'JOIN_PIN_INVALID',
  joinPinRateLimited: 'JOIN_PIN_RATE_LIMITED',
  // TECH-867: a live poll edited after Start; a live poll's place or audience refused.
  liveAlreadyStarted: 'LIVE_POLL_ALREADY_STARTED',
  placeInvalid: 'POLL_PLACE_INVALID',
} as const;

export type PollErrorCode = (typeof POLL_ERROR_CODES)[keyof typeof POLL_ERROR_CODES];

/** The envelope's code, or `undefined` (a network failure, a string detail, a validation list). */
export const pollErrorCode = (error: unknown): string | undefined => getErrorCode(error);

/** The live poll's codes (CONTRACT § 4.0, BE-L3-fix2). The 409s carry the fresh state in `details.state`. */
export const LIVE_ERROR_CODES = {
  positionMismatch: 'LIVE_POSITION_MISMATCH',
  moveInvalid: 'LIVE_MOVE_INVALID',
  /** Back from the results or Reopen after retention made the guest answers anonymous. */
  archived: 'LIVE_POLL_ARCHIVED',
  /** An answer to a question that is not the open one (E1). */
  questionClosed: 'LIVE_QUESTION_CLOSED',
  /** A big screen look that changes anything but Screen 2's colour once the poll has started. */
  screenLookLocked: 'LIVE_SCREEN_LOOK_LOCKED',
  cannotAnswer: 'LIVE_CANNOT_ANSWER',
  rateLimited: 'LIVE_RATE_LIMITED',
  /** 503 on `/open`: the server has no guest-token secret (fail closed); guests read only and sign in to answer. */
  guestsUnavailable: 'LIVE_GUESTS_UNAVAILABLE',
  /** 403 on `/claim`: the claim belongs to another sign-in's email (U2); nothing attached. */
  claimForbidden: 'LIVE_CLAIM_FORBIDDEN',
  /** 409 on `/join`: the poll has no join Space. */
  noJoinSpace: 'LIVE_NO_JOIN_SPACE',
} as const;

/** The refusals whose `details.state` is the poll as the server holds it now. */
const STATE_REFUSALS: readonly string[] = [
  LIVE_ERROR_CODES.positionMismatch,
  LIVE_ERROR_CODES.moveInvalid,
  LIVE_ERROR_CODES.archived,
  LIVE_ERROR_CODES.questionClosed,
  LIVE_ERROR_CODES.screenLookLocked,
];

const looksLikeLiveState = (value: unknown): value is LiveState =>
  value !== null &&
  typeof value === 'object' &&
  typeof (value as { live_code?: unknown }).live_code === 'string' &&
  typeof (value as { live_state?: unknown }).live_state === 'string' &&
  typeof (value as { version?: unknown }).version === 'number';

/**
 * A move or an answer refused because the poll moved on (another device, a double press, a retry, an archived
 * poll, a question that closed): the state the server holds now, to show instead of retrying. `undefined` for
 * any other failure.
 */
export const liveStateFromRefusal = (error: unknown): LiveState | undefined => {
  const code = getErrorCode(error);
  if (code === undefined || !STATE_REFUSALS.includes(code)) return undefined;
  const state = getErrorDetails(error)?.state;
  return looksLikeLiveState(state) ? state : undefined;
};

/** The Space a join refusal names (`details.space_id`), or null when it names none (§ 9.1). */
export const pollErrorSpaceId = (error: unknown): string | null => getDetailsSpaceId(error);
